import * as React from 'react';
import { NextPage } from 'next';
import { Dispatch } from 'redux';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as url from 'url';

import config from 'config';
import { ReduxState } from 'ducks';
import { wrapper } from 'ducks';
import { fetchConfigForHostname } from 'lib/util/fetchConfigForHostname';
import { CurrentUrlContext } from 'contexts/CurrentUrlContext';
import { BookingWidgetProductDetails } from 'components/BookingWidgetProductDetails/BookingWidgetProductDetails';
import { BookingWidgetLayout } from 'components/BookingWidgetLayout/BookingWidgetLayout';
import {
  fetchProductServerSide,
  fetchSettingsServerSide,
  fetchCustomPagesServerSide,
  fetchCustomTopPageServerSide,
} from 'ducks/server';
import { fetchReviews } from 'ducks/universal/reviews';
import { withSSR } from 'withSSR';
import { getI18nServerSideProps } from 'i18n';
import { detectLocale } from 'i18n-server';
import { selectLanguageOptions } from 'ducks/server/settings';
import { languageOptionMap } from 'lib/util/i18nHelpers';
import { absoluteUrl } from 'lib/util/absoluetUrl';
import { findPMPLinkForLocale } from 'lib/util/findPMPLinkForLocale';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';
import { getWeekdayText } from 'lib/util/getWeekdayText';
import { selectBookingWidgetHeaderDescription } from 'lib/util/bookingWidgetDesign';
import { getProductPageOgpTags } from 'lib/util/getProductPageOgpTags';
import { Product } from 'models/product';
import { toProductShape } from 'lib/util/productShape';

interface Props {
  apiKey: string;
  host: string;
  ogpPromotionId: string;
  showOgpHighlight: boolean;
  product?: Product;
  noIndex?: boolean;
}

const BookingWidgetProductDetailsPage: NextPage<Props> = ({
  host,
  ogpPromotionId,
  showOgpHighlight,
  product,
  noIndex,
}) => {
  const router = useRouter();
  const url = `${host}${router.asPath}`;

  const { t, i18n } = useTranslation();
  const headerText = useSelector(selectBookingWidgetHeaderDescription);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  const currencyFormatter = useCurrencyFormatter();
  const twitter = (settings?.sns_ids || []).find((snsId) => {
    return snsId?.key === 'twitter';
  });
  const description = product?.description || '';
  const dayOfWeek = product?.operating_days_of_week.length
    ? product.operating_days_of_week.map((weekday) => getWeekdayText(weekday, t)).join(', ')
    : '';
  let lowestPrice = product?.lowest_price_gross || '';
  if (lowestPrice) {
    lowestPrice = currencyFormatter.format(lowestPrice);
  }
  let ogpTags;
  if (product) {
    const productShape = toProductShape(product);
    ogpTags = getProductPageOgpTags(
      productShape,
      showOgpHighlight || false,
      ogpPromotionId || '',
      headerText,
      settings?.supplier_name || '',
      i18n.language || 'ja-JP',
      t,
      currencyFormatter
    );
  }
  //

  return (
    <>
      <CurrentUrlContext.Provider
        value={{
          url,
        }}
      >
        <Head>
          {config.enablePartnership && noIndex && <meta name="robots" content="noindex" />}
          <meta property="og:url" content={url} />
          <meta property="og:type" content={'article'} />
          <meta name="twitter:card" content={'summary_large_image'} />
          {ogpTags &&
            Object.entries(ogpTags).map(([key, value]) => (
              <meta key={key} property={key} content={value} />
            ))}
          {dayOfWeek && (
            <meta
              name="nutmeg:operating_days_of_week"
              content={t('Days of Week') + ':' + dayOfWeek}
            />
          )}
          {lowestPrice && <meta name="nutmeg:lowest_price_gross" content={lowestPrice} />}
          {description && <meta name="description" content={description} />}
          {twitter && twitter?.id && <meta name="twitter:site" content={`@${twitter.id}`} />}
        </Head>
        <BookingWidgetLayout>
          <BookingWidgetProductDetails />
        </BookingWidgetLayout>
      </CurrentUrlContext.Provider>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  async ({ params, req, res, store }) => {
    const hostname = req.headers?.host?.split(':')[0] || '';
    const { apiKey } = await fetchConfigForHostname(hostname);

    const productId = params?.id as string;

    let locale = await detectLocale(req, res);

    // Fetch settings using detected language
    const dispatch: Dispatch<any> = store.dispatch;
    await dispatch(fetchSettingsServerSide(apiKey, params?.id as string, locale));

    // Verify that the detected language is in the supplier's supported languages. If it isn't,
    // default to the first supported language and refetch settings.
    const langOptions = selectLanguageOptions(store.getState());
    if (langOptions.length > 0 && !langOptions.some((langOpt) => langOpt.iso === locale)) {
      locale = langOptions[0].iso;
      await dispatch(fetchSettingsServerSide(apiKey, params?.id as string, locale));
    }

    // If there are private marketplaces but we are viewing as booking widget, redirect to a private marketplace.
    const pmpEnabled = !!store.getState().server.settings.all.private_marketplace;
    const pmpLinks = store.getState().server.settings.all.private_marketplace_links;
    if (!pmpEnabled && (pmpLinks ?? []).length > 0) {
      const pmpLink = findPMPLinkForLocale(pmpLinks, locale);
      if (pmpLink) {
        return {
          redirect: {
            destination: `${pmpLink.url}/products/${productId}`,
            permanent: false,
          },
        };
      }
    }

    const fetchRequests = [
      dispatch(fetchProductServerSide(apiKey, productId, locale)),
      dispatch(
        fetchReviews(apiKey, locale, {
          productIds: [productId],
          pageSize: 5,
        })
      ),
      dispatch(fetchCustomPagesServerSide(apiKey, locale)),
    ];
    const isCustomTopPageEnabled = store.getState().server.settings.all?.is_custom_top_page_enabled;

    if (isCustomTopPageEnabled) {
      fetchRequests.push(dispatch(fetchCustomTopPageServerSide(apiKey, locale)));
    }

    await Promise.all(fetchRequests);

    const product = store.getState().server.product.product;
    if (
      product?.booking_widget_settings?.blacklisted_languages?.some(
        (language) => languageOptionMap[language as keyof typeof languageOptionMap]?.iso === locale
      ) ||
      !product?.booking_widget_settings?.is_visible
    ) {
      return {
        redirect: {
          destination: '/',
          permanent: false,
        },
      };
    }

    const i18nProps = await getI18nServerSideProps(locale);

    if (langOptions.length === 1) {
      (req as any).locale = langOptions[0].iso;
    } else {
      const sourceLanguage = product?.source_language || 'EN_US';
      (req as any).locale = languageOptionMap[sourceLanguage]?.iso;
    }

    const queries = url.parse(req.url || '', true).query;
    const ogpPromotionId = queries?.promo && typeof queries.promo === 'string' ? queries.promo : '';
    const showOgpHighlight = typeof queries?.highlight === 'string';

    const noIndex = product?.supplier_id !== store.getState().server.settings.all.supplier_id;

    return {
      props: {
        apiKey,
        host: absoluteUrl(req),
        ogpPromotionId,
        showOgpHighlight,
        product,
        noIndex,
        ...i18nProps,
      },
    };
  }
);

export default withSSR()(BookingWidgetProductDetailsPage);
